/**
 * Chat Service: backend REST API only.
 * Replaces the Stream Chat SDK entirely. Live updates come from the caller
 * polling (ChatInterface uses setInterval), not a realtime socket.
 *
 * Backend response format: { code, message, result: { success, data } }
 */
import { api } from '../utils';

/** Unwrap backend response: { code, message, result: { success, data } } */
function unwrap(response: any): any {
  return response?.data?.result?.data ?? response?.data?.data ?? response?.data?.result ?? response?.data;
}

export interface ChatConversation {
  id: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    avatar: string | null;
  } | null;
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: string;
    type: string;
  } | null;
  unreadCount: number;
  matchId: string | null;
  /** Multi-event: event the underlying match belongs to (null when unmatched or legacy). */
  eventId?: string | null;
  eventName?: string | null;
  createdAt: string;
  lastMessageAt: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'system';
  read_at: string | null;
  created_at: string;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_size?: number | null;
}

/**
 * Get all conversations for the current user.
 */
export const getConversations = async (): Promise<ChatConversation[]> => {
  const response = await api.get('/chat/conversations');
  return unwrap(response) || [];
};

/**
 * Create or get a conversation with another user.
 */
export const getOrCreateConversation = async (
  otherUserId: string,
  matchId?: string,
): Promise<string> => {
  const response = await api.post('/chat/conversations', { otherUserId, matchId });
  const data = unwrap(response);
  return data?.conversationId || data?.id;
};

/**
 * Get messages for a conversation with cursor-based pagination.
 */
export const getMessages = async (
  conversationId: string,
  limit: number = 50,
  before?: string,
): Promise<{ messages: ChatMessage[]; hasMore: boolean }> => {
  const params: any = { limit };
  if (before) params.before = before;
  const response = await api.get(`/chat/conversations/${conversationId}/messages`, { params });
  return unwrap(response) || { messages: [], hasMore: false };
};

/**
 * Send a message in a conversation.
 */
export const sendMessage = async (
  conversationId: string,
  content: string,
  messageType: 'text' | 'image' | 'system' = 'text',
  attachmentUrl?: string,
  attachmentName?: string,
  attachmentSize?: number,
): Promise<ChatMessage> => {
  const response = await api.post(`/chat/conversations/${conversationId}/messages`, {
    content,
    messageType,
    attachmentUrl,
    attachmentName,
    attachmentSize,
  });
  return unwrap(response);
};

/**
 * Upload a chat attachment to the backend (S3) and return the public URL.
 */
export const uploadAttachment = async (
  file: File,
  conversationId: string,
): Promise<{ url: string; name: string; size: number }> => {
  // Jun-19 (a user V1 test): route chat attachments through the backend S3
  // endpoint (POST /chat/upload-attachment), NOT Supabase Storage. The backend
  // migrated chat attachments to production S3 (Apr-20), but this WEB
  // uploader still pushed to the staging Supabase `chat-attachments` bucket,
  // which fails on prod, so images/documents silently never sent. Mirrors the
  // avatar upload (multipart -> backend).
  const formData = new FormData();
  formData.append('file', file);
  formData.append('conversationId', conversationId);

  const response = await api.post('/chat/upload-attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const data = unwrap(response);
  if (!data?.url) throw new Error('Attachment upload failed');

  return {
    url: data.url,
    name: file.name,
    size: file.size,
  };
};

/**
 * Mark all messages in a conversation as read.
 */
export const markAsRead = async (conversationId: string): Promise<void> => {
  await api.patch(`/chat/conversations/${conversationId}/read`);
};

/**
 * Get total unread count across all conversations.
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get('/chat/unread-count');
  const data = unwrap(response);
  return data?.unreadCount || 0;
};

/**
 * Start a direct message with a matched user.
 * Creates the conversation and returns the conversation ID.
 */
export const startDirectMessage = async (
  otherUserId: string,
  matchId?: string,
): Promise<string> => {
  return getOrCreateConversation(otherUserId, matchId);
};

/**
 * Block a user.
 */
export const blockUser = async (userId: string): Promise<void> => {
  await api.post('/chat/block', { userId });
};

/**
 * Unblock a user.
 */
export const unblockUser = async (userId: string): Promise<void> => {
  await api.delete(`/chat/block/${userId}`);
};

/**
 * Report a user.
 */
export const reportUser = async (
  userId: string,
  reason: string,
  details?: string,
  conversationId?: string,
): Promise<void> => {
  await api.post('/chat/report', { userId, reason, details, conversationId });
};

/**
 * Delete a conversation.
 */
export const deleteConversation = async (conversationId: string): Promise<void> => {
  await api.delete(`/chat/conversations/${conversationId}`);
};

// Legacy compatibility exports (used by PrivateLayout etc.)
export const getChatClient = () => null; // Stream Chat removed
export const setChatClient = () => {}; // No-op
export const initializeChatClient = async () => null; // No-op
export const disconnectChatClient = async () => {}; // No-op
